import { ScrollReveal } from '@/components/motion';
import { cn } from '@ibas/ui';

interface AccreditationLogo {
  src: string;
  alt: string;
}

// Five marks only. EduQua is deliberately excluded from this general band —
// its accreditation mark is reserved for use on its own dedicated page, per
// the ibas-brand logo-usage rules.
const LOGOS: readonly AccreditationLogo[] = [
  { src: '/accreditations/isasahe.png', alt: 'ISASAHE institutional accreditation' },
  {
    src: '/accreditations/ieac.png',
    alt: 'IEAC — International Education Accreditation Council, accredited',
  },
  {
    src: '/accreditations/swiss-register.png',
    alt: 'Swiss Private School Register (Privatschulregister)',
  },
  { src: '/accreditations/qs-top-universities.png', alt: 'QS Top Universities' },
  { src: '/accreditations/qs-stars.png', alt: 'QS Stars overall rating' },
];

export interface AccreditationLogosProps {
  className?: string;
}

/**
 * AccreditationLogos — a light, compact band presenting the five real
 * accreditation/ranking marks side by side as institutional proof.
 *
 * Motion intent: the whole row rises in as a single quiet ScrollReveal beat
 * (stagger across the five logos) as the band enters view — confident, not
 * showy. Each mark sits at reduced grayscale/opacity by default and resolves
 * to full colour and full opacity on hover/focus for a premium, tactile
 * micro-interaction. Under reduced motion the reveal is skipped (logos render
 * in their final, visible position immediately, per ScrollReveal's own
 * reduced-motion handling) and every mark renders at full colour/opacity from
 * the start — grayscale is a hover-only affordance, never the resting state
 * for reduced-motion or touch users, since touch has no hover to resolve it.
 */
export function AccreditationLogos({ className }: AccreditationLogosProps) {
  return (
    <section
      aria-labelledby="accreditation-logos-heading"
      className={cn('bg-background px-6 py-14 sm:py-16 lg:px-16', className)}
    >
      <div className="mx-auto max-w-6xl text-center">
        <p className="font-label text-xs tracking-[0.35em] text-brand-gold uppercase sm:text-sm">
          Recognised and accredited
        </p>
        <h2
          id="accreditation-logos-heading"
          className="mt-3 font-display text-2xl text-foreground sm:text-3xl"
        >
          Membership, recognition &amp; accreditations
        </h2>

        <ScrollReveal
          stagger={0.08}
          className="mx-auto mt-10 grid grid-cols-2 items-center justify-items-center gap-x-8 gap-y-8 sm:mt-12 sm:gap-x-10 sm:gap-y-10 lg:grid-cols-5 lg:gap-x-12"
        >
          {LOGOS.map((logo) => (
            <div key={logo.src} className="flex h-16 w-full items-center justify-center sm:h-20">
              {/* Official marks — never recoloured, stretched or rotated.
                  Grayscale-by-default resolves to full colour on hover/focus;
                  touch and reduced-motion users see full colour immediately. */}
              <img
                src={logo.src}
                alt={logo.alt}
                loading="lazy"
                className="max-h-14 w-auto object-contain grayscale-[60%] opacity-70 transition-all duration-300 ease-out hover:grayscale-0 hover:opacity-100 focus-visible:grayscale-0 focus-visible:opacity-100 motion-reduce:grayscale-0 motion-reduce:opacity-100 sm:max-h-16 [@media(hover:none)]:grayscale-0 [@media(hover:none)]:opacity-100"
              />
            </div>
          ))}
        </ScrollReveal>
      </div>
    </section>
  );
}
